import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Activity,
  Server,
  Database,
  Cpu,
  HardDrive,
  Zap,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  TrendingUp,
  Wifi,
  Globe
} from 'lucide-react'
import { useNotification } from '../../contexts/NotificationContext'

interface SystemMetrics {
  status: 'healthy' | 'warning' | 'critical'
  uptime: number
  cpu: {
    usage: number
    cores: number
    temperature?: number
  }
  memory: {
    total: number
    used: number
    free: number
    percentage: number
  }
  disk: {
    total: number
    used: number
    free: number
    percentage: number
  }
  database: {
    status: 'online' | 'offline'
    connections: number
    maxConnections: number
    responseTime: number
  }
  api: {
    status: 'online' | 'offline'
    requestsPerMinute: number
    averageResponseTime: number
    errorRate: number
  }
  lastBackup: string
  lastUpdated: string
}

const API_BASE = import.meta.env.VITE_API_URL || ''

export default function SystemHealthPage() {
  const navigate = useNavigate()
  const { success, error: showError } = useNotification()
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    fetchSystemHealth()
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchSystemHealth, 30000)
    return () => clearInterval(interval)
  }, [])

  const fetchSystemHealth = async () => {
    try {
      setRefreshing(true)
      const response = await fetch(`${API_BASE}/api/system-health`)
      const data = await response.json()

      if (data.success) {
        setMetrics(data.data)
      } else {
        showError('Error', data.error)
      }
    } catch (error) {
      console.error('Error fetching system health:', error)
      showError('Error', 'Failed to fetch system health')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'online':
        return '#00FF00'
      case 'warning':
        return '#FFA500'
      case 'critical':
      case 'offline':
        return '#FF0000'
      default:
        return '#6B7280'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'online':
        return <CheckCircle style={{ width: 20, height: 20, color: '#00FF00' }} />
      case 'warning':
        return <AlertTriangle style={{ width: 20, height: 20, color: '#FFA500' }} />
      case 'critical':
      case 'offline':
        return <XCircle style={{ width: 20, height: 20, color: '#FF0000' }} />
      default:
        return <Activity style={{ width: 20, height: 20, color: '#6B7280' }} />
    }
  }

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400)
    const hours = Math.floor((seconds % 86400) / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${days}d ${hours}h ${minutes}m`
  }

  const formatBytes = (bytes: number) => {
    const gb = bytes / (1024 ** 3)
    return `${gb.toFixed(2)} GB`
  }

  if (loading) {
    return (
      <div style={{
        padding: '24px',
        background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
        minHeight: '100vh',
        fontFamily: 'Rajdhani, sans-serif',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <RefreshCw style={{ width: 48, height: 48, color: '#00FF00' }} className="animate-spin mx-auto mb-4" />
          <p style={{ color: '#E0E6F0', fontSize: '18px', fontWeight: 700 }}>
            LOADING SYSTEM DIAGNOSTICS...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '32px',
        borderBottom: '2px solid #00FF00',
        paddingBottom: '24px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div>
            <h1 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '48px',
              fontWeight: 700,
              color: '#00FF00',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              margin: 0,
              textShadow: '0 0 20px rgba(0, 255, 0, 0.6)'
            }}>
              SYSTEM HEALTH // DIAGNOSTICS
            </h1>
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '16px',
              color: '#8A95AA',
              margin: '8px 0 0 0',
              letterSpacing: '1px'
            }}>
              REAL-TIME SYSTEM MONITORING INTERFACE
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={fetchSystemHealth}
              disabled={refreshing}
              style={{
                background: 'linear-gradient(135deg, #00FF00, #00CC00)',
                border: '2px solid #00FF00',
                borderRadius: '6px',
                padding: '10px 20px',
                color: '#1A1F2E',
                fontSize: '14px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                cursor: refreshing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 0 20px rgba(0, 255, 0, 0.4)',
                opacity: refreshing ? 0.6 : 1
              }}
            >
              <RefreshCw style={{ width: 16, height: 16 }} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'REFRESHING' : 'REFRESH'}
            </button>
            <button
              onClick={() => navigate('/admin')}
              style={{
                background: 'linear-gradient(135deg, #00F0FF, #00B8D4)',
                border: '2px solid #00F0FF',
                borderRadius: '6px',
                padding: '10px 20px',
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 0 20px rgba(0, 240, 255, 0.4)'
              }}
            >
              <ArrowLeft style={{ width: 16, height: 16 }} />
              RETURN TO ADMIN
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats Bar */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '12px',
        marginBottom: '32px'
      }}>
        {[
          {
            label: 'SYSTEM STATUS',
            value: metrics?.status.toUpperCase() || 'UNKNOWN',
            color: getStatusColor(metrics?.status || 'unknown')
          },
          {
            label: 'UPTIME',
            value: metrics ? formatUptime(metrics.uptime) : 'N/A',
            color: '#00F0FF'
          },
          {
            label: 'CPU USAGE',
            value: `${metrics?.cpu.usage || 0}%`,
            color: (metrics?.cpu.usage || 0) > 80 ? '#FF0000' : (metrics?.cpu.usage || 0) > 60 ? '#FFA500' : '#00FF00'
          },
          {
            label: 'MEMORY',
            value: `${metrics?.memory.percentage || 0}%`,
            color: (metrics?.memory.percentage || 0) > 80 ? '#FF0000' : (metrics?.memory.percentage || 0) > 60 ? '#FFA500' : '#FFD93D'
          },
          {
            label: 'DISK',
            value: `${metrics?.disk.percentage || 0}%`,
            color: (metrics?.disk.percentage || 0) > 80 ? '#FF0000' : (metrics?.disk.percentage || 0) > 60 ? '#FFA500' : '#B91BFF'
          },
          {
            label: 'DATABASE',
            value: metrics?.database.status.toUpperCase() || 'UNKNOWN',
            color: getStatusColor(metrics?.database.status || 'offline')
          },
          {
            label: 'API STATUS',
            value: metrics?.api.status.toUpperCase() || 'UNKNOWN',
            color: getStatusColor(metrics?.api.status || 'offline')
          }
        ].map((stat, idx) => (
          <div key={idx} style={{
            background: 'linear-gradient(135deg, rgba(0, 255, 0, 0.05), rgba(0, 255, 0, 0.02))',
            border: '1px solid rgba(0, 255, 0, 0.3)',
            borderRadius: '6px',
            padding: '12px',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '11px',
              color: '#8A95AA',
              letterSpacing: '1px',
              marginBottom: '4px'
            }}>
              {stat.label}
            </div>
            <div style={{
              fontSize: '20px',
              fontWeight: 700,
              color: stat.color
            }}>
              {stat.value}
            </div>
          </div>
        ))}
      </div>

      {/* System Cards Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
        gap: '20px'
      }}>
        {/* CPU Card */}
        <div style={{
          background: 'linear-gradient(135deg, #1A1F2E, #242938)',
          border: '2px solid #FF6B35',
          borderRadius: '8px',
          padding: '24px',
          boxShadow: '0 0 20px rgba(255, 107, 53, 0.2)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100px',
            height: '100px',
            background: 'linear-gradient(135deg, transparent 50%, rgba(255, 107, 53, 0.2) 50%)',
            pointerEvents: 'none'
          }} />

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '16px'
          }}>
            <div style={{
              background: 'rgba(255, 107, 53, 0.2)',
              border: '2px solid #FF6B35',
              borderRadius: '8px',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Cpu style={{ width: 28, height: 28, color: '#FF6B35' }} />
            </div>
            {getStatusIcon('healthy')}
          </div>

          <h3 style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '24px',
            fontWeight: 700,
            color: '#FF6B35',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            margin: '0 0 8px 0',
            textShadow: '0 0 10px rgba(255, 107, 53, 0.6)'
          }}>
            CPU METRICS
          </h3>

          <div style={{
            display: 'flex',
            gap: '20px',
            paddingTop: '16px',
            borderTop: '1px solid rgba(138, 149, 170, 0.2)'
          }}>
            <div>
              <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
                USAGE
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#E0E6F0' }}>
                {metrics?.cpu.usage || 0}%
              </div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
                CORES
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#00FF00' }}>
                {metrics?.cpu.cores || 0}
              </div>
            </div>
          </div>
        </div>

        {/* Memory Card */}
        <div style={{
          background: 'linear-gradient(135deg, #1A1F2E, #242938)',
          border: '2px solid #FFD93D',
          borderRadius: '8px',
          padding: '24px',
          boxShadow: '0 0 20px rgba(255, 217, 61, 0.2)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100px',
            height: '100px',
            background: 'linear-gradient(135deg, transparent 50%, rgba(255, 217, 61, 0.2) 50%)',
            pointerEvents: 'none'
          }} />

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '16px'
          }}>
            <div style={{
              background: 'rgba(255, 217, 61, 0.2)',
              border: '2px solid #FFD93D',
              borderRadius: '8px',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Zap style={{ width: 28, height: 28, color: '#FFD93D' }} />
            </div>
            {getStatusIcon('healthy')}
          </div>

          <h3 style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '24px',
            fontWeight: 700,
            color: '#FFD93D',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            margin: '0 0 8px 0',
            textShadow: '0 0 10px rgba(255, 217, 61, 0.6)'
          }}>
            MEMORY STATUS
          </h3>

          <div style={{
            display: 'flex',
            gap: '20px',
            paddingTop: '16px',
            borderTop: '1px solid rgba(138, 149, 170, 0.2)'
          }}>
            <div>
              <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
                USAGE
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#E0E6F0' }}>
                {metrics?.memory.percentage || 0}%
              </div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
                USED/TOTAL
              </div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#E0E6F0' }}>
                {formatBytes(metrics?.memory.used || 0)} / {formatBytes(metrics?.memory.total || 0)}
              </div>
            </div>
          </div>
        </div>

        {/* Disk Card */}
        <div style={{
          background: 'linear-gradient(135deg, #1A1F2E, #242938)',
          border: '2px solid #B91BFF',
          borderRadius: '8px',
          padding: '24px',
          boxShadow: '0 0 20px rgba(185, 27, 255, 0.2)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100px',
            height: '100px',
            background: 'linear-gradient(135deg, transparent 50%, rgba(185, 27, 255, 0.2) 50%)',
            pointerEvents: 'none'
          }} />

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '16px'
          }}>
            <div style={{
              background: 'rgba(185, 27, 255, 0.2)',
              border: '2px solid #B91BFF',
              borderRadius: '8px',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <HardDrive style={{ width: 28, height: 28, color: '#B91BFF' }} />
            </div>
            {getStatusIcon('healthy')}
          </div>

          <h3 style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '24px',
            fontWeight: 700,
            color: '#B91BFF',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            margin: '0 0 8px 0',
            textShadow: '0 0 10px rgba(185, 27, 255, 0.6)'
          }}>
            DISK STORAGE
          </h3>

          <div style={{
            display: 'flex',
            gap: '20px',
            paddingTop: '16px',
            borderTop: '1px solid rgba(138, 149, 170, 0.2)'
          }}>
            <div>
              <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
                USAGE
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#E0E6F0' }}>
                {metrics?.disk.percentage || 0}%
              </div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
                FREE
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#00FF00' }}>
                {formatBytes(metrics?.disk.free || 0)}
              </div>
            </div>
          </div>
        </div>

        {/* Database Card */}
        <div style={{
          background: 'linear-gradient(135deg, #1A1F2E, #242938)',
          border: '2px solid #00F0FF',
          borderRadius: '8px',
          padding: '24px',
          boxShadow: '0 0 20px rgba(0, 240, 255, 0.2)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100px',
            height: '100px',
            background: 'linear-gradient(135deg, transparent 50%, rgba(0, 240, 255, 0.2) 50%)',
            pointerEvents: 'none'
          }} />

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '16px'
          }}>
            <div style={{
              background: 'rgba(0, 240, 255, 0.2)',
              border: '2px solid #00F0FF',
              borderRadius: '8px',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Database style={{ width: 28, height: 28, color: '#00F0FF' }} />
            </div>
            {getStatusIcon(metrics?.database.status || 'offline')}
          </div>

          <h3 style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '24px',
            fontWeight: 700,
            color: '#00F0FF',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            margin: '0 0 8px 0',
            textShadow: '0 0 10px rgba(0, 240, 255, 0.6)'
          }}>
            DATABASE
          </h3>

          <div style={{
            display: 'flex',
            gap: '20px',
            paddingTop: '16px',
            borderTop: '1px solid rgba(138, 149, 170, 0.2)'
          }}>
            <div>
              <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
                CONNECTIONS
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#E0E6F0' }}>
                {metrics?.database.connections || 0}/{metrics?.database.maxConnections || 0}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
                RESPONSE TIME
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#00FF00' }}>
                {metrics?.database.responseTime || 0}ms
              </div>
            </div>
          </div>
        </div>

        {/* API Card */}
        <div style={{
          background: 'linear-gradient(135deg, #1A1F2E, #242938)',
          border: '2px solid #6BCF7F',
          borderRadius: '8px',
          padding: '24px',
          boxShadow: '0 0 20px rgba(107, 207, 127, 0.2)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100px',
            height: '100px',
            background: 'linear-gradient(135deg, transparent 50%, rgba(107, 207, 127, 0.2) 50%)',
            pointerEvents: 'none'
          }} />

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '16px'
          }}>
            <div style={{
              background: 'rgba(107, 207, 127, 0.2)',
              border: '2px solid #6BCF7F',
              borderRadius: '8px',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Globe style={{ width: 28, height: 28, color: '#6BCF7F' }} />
            </div>
            {getStatusIcon(metrics?.api.status || 'offline')}
          </div>

          <h3 style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '24px',
            fontWeight: 700,
            color: '#6BCF7F',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            margin: '0 0 8px 0',
            textShadow: '0 0 10px rgba(107, 207, 127, 0.6)'
          }}>
            API SERVER
          </h3>

          <div style={{
            display: 'flex',
            gap: '20px',
            paddingTop: '16px',
            borderTop: '1px solid rgba(138, 149, 170, 0.2)'
          }}>
            <div>
              <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
                REQ/MIN
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#E0E6F0' }}>
                {metrics?.api.requestsPerMinute || 0}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
                AVG RESPONSE
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#00FF00' }}>
                {metrics?.api.averageResponseTime || 0}ms
              </div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
                ERROR RATE
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#E0E6F0' }}>
                {metrics?.api.errorRate || 0}%
              </div>
            </div>
          </div>
        </div>

        {/* Uptime Card */}
        <div style={{
          background: 'linear-gradient(135deg, #1A1F2E, #242938)',
          border: '2px solid #FF1493',
          borderRadius: '8px',
          padding: '24px',
          boxShadow: '0 0 20px rgba(255, 20, 147, 0.2)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100px',
            height: '100px',
            background: 'linear-gradient(135deg, transparent 50%, rgba(255, 20, 147, 0.2) 50%)',
            pointerEvents: 'none'
          }} />

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '16px'
          }}>
            <div style={{
              background: 'rgba(255, 20, 147, 0.2)',
              border: '2px solid #FF1493',
              borderRadius: '8px',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Clock style={{ width: 28, height: 28, color: '#FF1493' }} />
            </div>
            {getStatusIcon('healthy')}
          </div>

          <h3 style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '24px',
            fontWeight: 700,
            color: '#FF1493',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            margin: '0 0 8px 0',
            textShadow: '0 0 10px rgba(255, 20, 147, 0.6)'
          }}>
            SYSTEM UPTIME
          </h3>

          <div style={{
            display: 'flex',
            gap: '20px',
            paddingTop: '16px',
            borderTop: '1px solid rgba(138, 149, 170, 0.2)'
          }}>
            <div>
              <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
                UPTIME
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#E0E6F0' }}>
                {metrics ? formatUptime(metrics.uptime) : 'N/A'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
                LAST BACKUP
              </div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#00FF00' }}>
                {metrics?.lastBackup || 'N/A'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Footer */}
      <div style={{
        marginTop: '32px',
        background: 'linear-gradient(135deg, rgba(0, 255, 0, 0.1), rgba(0, 255, 0, 0.05))',
        border: '2px solid #00FF00',
        borderRadius: '8px',
        padding: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: getStatusColor(metrics?.status || 'unknown'),
              boxShadow: `0 0 10px ${getStatusColor(metrics?.status || 'unknown')}`,
              animation: 'pulse 2s infinite'
            }} />
            <span style={{ color: '#E0E6F0', fontSize: '14px', letterSpacing: '1px', fontWeight: 700 }}>
              {metrics?.status === 'healthy' && 'ALL SYSTEMS OPERATIONAL'}
              {metrics?.status === 'warning' && 'SYSTEM WARNING DETECTED'}
              {metrics?.status === 'critical' && 'CRITICAL SYSTEM ALERT'}
            </span>
          </div>
          <div style={{ fontSize: '12px', color: '#8A95AA', letterSpacing: '1px' }}>
            Last Updated: {metrics?.lastUpdated || 'N/A'}
          </div>
        </div>
        <div style={{ fontSize: '12px', color: '#8A95AA', letterSpacing: '1px' }}>
          Auto-refresh: 30s
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  )
}
